import { EarningsInsight } from '@api/api/earnings-insights';
import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { ErrorBoundaryWrapper } from '@terminal/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { InsightDrawerHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerHeaderContent';
import { useGetEarningById } from '@terminal/react-query-hooks/earnings/useGetEarningById';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { CardType } from '@terminal/views/insight-feed/components/insight-feed-card/InsightFeedCard';
import { EarningsInsightPill } from '@terminal/widgets/earnings-insight-drawer/earnings-insight-widget/components/earnings-insight-pill/EarningsInsightPill';
import { useEarningsInsightDrawer } from '@terminal/widgets/earnings-insight-drawer/useEarningsInsightDrawer';
import { useTranslation } from 'react-i18next';

import { EarningsDateLabel } from '../../components/earnings-date-label/EarningsDateLabel';
import { PublishedDate } from '../../components/published-date/PublishedDate';
import { EarningsExpectationsTable } from './earnings-expectations-table/EarningsExpectationsTable';
import styles from './PreviewOverview.module.scss';
import { TargetPriceSection } from './target-price-section/TargetPriceSection';

export interface PreviewOverviewProps {
  title: string;
  activeEntityTag: string;
  publishedAt: string;
  previewEarningsInsight?: EarningsInsight;
  earningsId?: string;
  showDrawerHeader?: boolean;
}

export const PreviewOverview = ({
  title,
  activeEntityTag,
  publishedAt,
  previewEarningsInsight,
  earningsId: earningsIdProp,
  showDrawerHeader = false,
}: PreviewOverviewProps) => {
  const { t } = useTranslation(['earnings']);
  const { earningsId: drawerEarningsId } = useEarningsInsightDrawer();
  const earningsId = earningsIdProp ?? drawerEarningsId;

  const {
    data: earningsData,
    isFetching,
    isError,
    error,
  } = useGetEarningById(earningsId ?? '');

  if (isFetching) {
    return (
      <div className={styles.loaderWrapper}>
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: '100px',
                borderRadius: '8px',
              },
            },
          ]}
        />
      </div>
    );
  }

  if (isError) {
    throw new Error(error?.message);
  }

  return (
    <div className={styles.container}>
      {showDrawerHeader ? (
        <InsightDrawerHeaderContent
          cardType={CardType.EarningsPreview}
          publishedAt={publishedAt}
          title={title}
        />
      ) : (
        <>
          <div className={styles.pillWrapper}>
            {previewEarningsInsight && (
              <EarningsInsightPill
                entityTag={activeEntityTag}
                earningsInsight={previewEarningsInsight}
                isClickable={false}
                isPreview
                source={POSTHOG_TRACKING_SOURCES.EARNINGS_INSIGHT_DRAWER}
              />
            )}
            <PublishedDate publishedAt={publishedAt} isPreview />
          </div>
          <p className={styles.title}>{title}</p>
        </>
      )}
      <div className={styles.wrapper}>
        {earningsData && (
          <>
            <EarningsDateLabel
              date={earningsData?.reporting_date}
              label={t(
                'earningsInsight.drawer.earningsPreview.earningsReleased'
              )}
            />
            <EarningsExpectationsTable earningsData={earningsData} />
          </>
        )}
        <ErrorBoundaryWrapper>
          <TargetPriceSection entityTag={activeEntityTag} />
        </ErrorBoundaryWrapper>
      </div>
    </div>
  );
};
